import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, DatePicker, Input, Row, Spin, Switch, message, notification } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  SaveOutlined,
  PrinterOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import styled from "styled-components";
import { TUser } from "../../../redux-modules/declarations/maps/auth";
import apiClient from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../constants/api";

// ─────────────────────────────────────────────────────────────────────────────
// Colore principale
// ─────────────────────────────────────────────────────────────────────────────

const MAIN = "#41589E";
const MAIN_LIGHT = "#E3E8F3"; // tint per dipartimento
const MAIN_LIGHTER = "#F0F3F9"; // tint per hover utente
const CELL_FILLED = "#4D9069"; // verde scuro: corpo/estremi piatti del turno
const CELL_FILLED_HOVER = "#427E5C";
const CELL_FILLED_LIGHT = "#92D050"; // verde chiaro: estremo su mezz'ora (xx:30)
const CELL_FILLED_LIGHT_HOVER = "#82BC47";
const CELL_HOVER = "#DDE3F0"; // cella vuota in hover
const CELL_ADDED = "#F6E787"; // cella aggiunta in questa sessione (non ancora salvata)
const CELL_ADDED_HOVER = "#DDCF79";
const CELL_REMOVED = "#F2D2D2"; // cella rimossa in questa sessione (non ancora salvata)
const CELL_REMOVED_HOVER = "#E9BEBE";
const NIGHT_HEADER_BG = "#EEF1F8"; // header delle colonne dopo mezzanotte
const NIGHT_CELL_BG = "#F7F9FC"; // sfondo cella notturna (vuota)

// ─────────────────────────────────────────────────────────────────────────────
// Tipi
// ─────────────────────────────────────────────────────────────────────────────

type ShiftMap = Record<string, boolean>; // chiave: "YYYY-MM-DD_userId_hour"

interface HourRange {
  start: string; // "HH:MM"
  end: string; // "HH:MM" (esclusivo)
}

// Forma (parziale) di un turno restituito dal server
interface ServerShift {
  user: { id: number };
  shift_type: { time_from: string; time_to: string; is_notturno?: number };
  is_notturno?: number;
  date: string;
}

interface GroupedData {
  groupName: string;
  departments: {
    deptName: string;
    users: TUser[];
  }[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility
// ─────────────────────────────────────────────────────────────────────────────

const SLOT_MINUTES = 30; // granularità della griglia
const SLOTS_PER_HOUR = 60 / SLOT_MINUTES; // 2 slot per ora (:00 e :30)
const DAY_SLOTS = 24 * SLOTS_PER_HOUR; // 48 slot del giorno (00:00..23:30)
const NIGHT_HOURS_COUNT = 6; // ore oltre mezzanotte mostrabili (00..05)
const NIGHT_SLOTS = NIGHT_HOURS_COUNT * SLOTS_PER_HOUR; // 12
// Universo completo degli slot selezionabili (giorno + estensione notturna).
const ALL_SLOTS = Array.from({ length: DAY_SLOTS + NIGHT_SLOTS }, (_, i) => i);
const NIGHT_FROM_SLOT = DAY_SLOTS; // primo slot "oltre mezzanotte" (48 = 00:00 +1g)
const MIDNIGHT_MIN = 24 * 60; // 1440

const isNightSlot = (s: number): boolean => s >= NIGHT_FROM_SLOT;
const isHourStart = (s: number): boolean => s % SLOTS_PER_HOUR === 0; // slot dei :00
// "00"/"30" → minuti dello slot
const slotMinuteLabel = (s: number): string =>
  String((s % SLOTS_PER_HOUR) * SLOT_MINUTES).padStart(2, "0");
// etichetta dell'ora per uno slot: 0..23 normale, notturni → 00..05
const slotHourLabel = (s: number): string =>
  String(Math.floor((s * SLOT_MINUTES) / 60) % 24).padStart(2, "0");

// Data locale in formato YYYY-MM-DD (NON usare toISOString: converte in UTC
// e su fuso orario italiano riporterebbe il giorno precedente alla mezzanotte)
function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function shiftKey(date: Date, userId: string, slot: number): string {
  return `${dateKey(date)}_${userId}_${slot}`;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

// slot → "HH:MM". La fine esclusiva a mezzanotte resta "24:00"; gli slot oltre
// la mezzanotte si avvolgono al giorno dopo (00:30, 01:00, ...).
function slotToTime(slot: number): string {
  const minutes = slot * SLOT_MINUTES;
  const base = minutes <= MIDNIGHT_MIN ? minutes : minutes - MIDNIGHT_MIN;
  const hh = Math.floor(base / 60);
  const mm = base % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// "HH:MM" → minuti dalla mezzanotte. "00:00" come FINE = fine giornata → 1440.
function toMinutes(time: string, asEnd = false): number {
  const total = parseInt(time.slice(0, 2), 10) * 60 + parseInt(time.slice(3, 5), 10);
  return asEnd && total === 0 ? MIDNIGHT_MIN : total;
}

// Totale ore (decimale, min 1 / max 2 cifre, formato italiano) da un numero di slot.
function formatHours(slotsCount: number): string {
  const hours = (slotsCount * SLOT_MINUTES) / 60;
  return hours.toLocaleString("it-IT", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
}

// Comprime una lista di slot in range contigui.
// Es: [18,19,20, 28,29] → 09:00-10:30, 14:00-15:00
function slotsToRanges(slots: number[]): HourRange[] {
  if (slots.length === 0) return [];
  const sorted = [...slots].sort((a, b) => a - b);

  const ranges: HourRange[] = [];
  let startS = sorted[0];
  let prevS = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const s = sorted[i];
    if (s === prevS + 1) {
      prevS = s; // slot contiguo, estende il range corrente
    } else {
      ranges.push({ start: slotToTime(startS), end: slotToTime(prevS + 1) });
      startS = s;
      prevS = s;
    }
  }
  ranges.push({ start: slotToTime(startS), end: slotToTime(prevS + 1) });
  return ranges;
}

// Converte la risposta del server nella ShiftMap piatta a slot. time_to è
// esclusivo. I turni notturni (fine ≤ inizio, o is_notturno) proseguono nel
// giorno dopo → slot 48+. Gli orari non allineati ai 30 min vengono ampliati
// (inizio per difetto, fine per eccesso) così la cella contiene sempre il
// turno reale; segnalo se è successo, per avvisare l'utente.
function serverToSlotMap(
  items: ServerShift[],
  date: Date
): { map: ShiftMap; hasMisaligned: boolean; hasNight: boolean } {
  const map: ShiftMap = {};
  let hasMisaligned = false;
  let hasNight = false;

  items.forEach((item) => {
    const uid = String(item.user.id);
    const startMin = toMinutes(item.shift_type.time_from);
    let endMin = toMinutes(item.shift_type.time_to, true); // "00:00" → 1440

    const overnight =
      !!item.is_notturno || !!item.shift_type.is_notturno || endMin <= startMin;
    if (overnight && endMin <= startMin) {
      endMin += MIDNIGHT_MIN; // la fine cade nel giorno successivo
    }

    if (startMin % SLOT_MINUTES !== 0 || endMin % SLOT_MINUTES !== 0) {
      hasMisaligned = true;
    }

    const startSlot = Math.floor(startMin / SLOT_MINUTES); // inizio per difetto
    const endSlot = Math.ceil(endMin / SLOT_MINUTES); // fine per eccesso

    for (let s = startSlot; s < endSlot && s < ALL_SLOTS.length; s++) {
      map[shiftKey(date, uid, s)] = true;
      if (s >= NIGHT_FROM_SLOT) hasNight = true;
    }
  });

  return { map, hasMisaligned, hasNight };
}

// Raggruppa utenti per gruppo → dipartimento
function groupUsers(users: TUser[]): GroupedData[] {
  const map: Record<string, Record<string, TUser[]>> = {};

  users.forEach((u) => {
    const groupName = u.groups?.[0]?.name?.trim() ?? "Senza gruppo";
    const deptName = u.department?.name ?? "Senza dipartimento";

    if (!map[groupName]) map[groupName] = {};
    if (!map[groupName][deptName]) map[groupName][deptName] = [];
    map[groupName][deptName].push(u);
  });

  const collator = new Intl.Collator("it", { sensitivity: "base" });

  return Object.entries(map)
    .sort(([a], [b]) => collator.compare(a, b)) // gruppi in ordine alfabetico
    .map(([groupName, depts]) => ({
      groupName,
      departments: Object.entries(depts)
        .sort(([a], [b]) => collator.compare(a, b)) // dipartimenti in ordine alfabetico
        .map(([deptName, users]) => ({
          deptName,
          users: [...users].sort((a, b) =>
            collator.compare(`${a.surname} ${a.name}`, `${b.surname} ${b.name}`)
          ),
        })),
    }));
}

// Filtra gli utenti per testo (gruppo, dipartimento o dipendente)
function filterUsers(users: TUser[], term: string): TUser[] {
  const q = term.trim().toLowerCase();
  if (!q) return users;
  return users.filter((u) => {
    const groupName = u.groups?.[0]?.name?.toLowerCase() ?? "";
    const deptName = u.department?.name?.toLowerCase() ?? "";
    const fullName = `${u.surname} ${u.name}`.toLowerCase();
    return groupName.includes(q) || deptName.includes(q) || fullName.includes(q);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Cella turno
// ─────────────────────────────────────────────────────────────────────────────

interface ShiftCellProps {
  userId: string;
  slot: number;
  filled: boolean;
  modified: boolean;
  light: boolean; // estremo su mezz'ora → verde chiaro
  label: string | null; // orario da mostrare (estremi del turno)
  night: boolean;
  midnight: boolean;
  hourStart: boolean;
  onMouseDown: (userId: string, slot: number) => void;
  onMouseEnter: (userId: string, slot: number) => void;
}

const ShiftCell = React.memo(function ShiftCell({
  userId,
  slot,
  filled,
  modified,
  light,
  label,
  night,
  midnight,
  hourStart,
  onMouseDown,
  onMouseEnter,
}: ShiftCellProps) {
  return (
    <StyledCell
      filled={filled}
      modified={modified}
      light={light}
      night={night}
      midnight={midnight}
      hourStart={hourStart}
      onMouseDown={() => onMouseDown(userId, slot)}
      onMouseEnter={() => onMouseEnter(userId, slot)}
    >
      {label && <CellLabel darkText={light || modified}>{label}</CellLabel>}
    </StyledCell>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Componente principale
// ─────────────────────────────────────────────────────────────────────────────

interface ShiftGridProps {
  userList: TUser[];
}

export const ShiftGrid: React.FC<ShiftGridProps> = ({ userList }) => {
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [shifts, setShifts] = useState<ShiftMap>({});
  const [baseline, setBaseline] = useState<ShiftMap>({}); // stato caricato dal server, per il diff
  const [search, setSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [collapsedDepts, setCollapsedDepts] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNight, setShowNight] = useState(false); // colonne dopo mezzanotte

  const isDragging = useRef(false);
  const dragValue = useRef<boolean | null>(null);

  // Raggruppamento + filtro
  const grouped = useMemo(() => groupUsers(filterUsers(userList, search)), [userList, search]);

  // Slot visibili: solo giorno, oppure giorno + estensione notturna.
  const visibleSlots = useMemo(
    () => (showNight ? ALL_SLOTS : ALL_SLOTS.slice(0, DAY_SLOTS)),
    [showNight]
  );

  // Gruppi-ora per la prima riga dell'header (un'etichetta ogni SLOTS_PER_HOUR slot).
  const hourGroups = useMemo(() => {
    const groups: { startSlot: number; label: string; night: boolean; midnight: boolean }[] = [];
    for (let i = 0; i < visibleSlots.length; i += SLOTS_PER_HOUR) {
      const startSlot = visibleSlots[i];
      groups.push({
        startSlot,
        label: slotHourLabel(startSlot),
        night: isNightSlot(startSlot),
        midnight: startSlot === NIGHT_FROM_SLOT,
      });
    }
    return groups;
  }, [visibleSlots]);

  // Mouse up globale per terminare il drag
  useEffect(() => {
    const onMouseUp = () => {
      isDragging.current = false;
      dragValue.current = null;
    };
    document.addEventListener("mouseup", onMouseUp);
    return () => document.removeEventListener("mouseup", onMouseUp);
  }, []);

  // Caricamento turni del giorno selezionato
  useEffect(() => {
    let cancelled = false; // ← nuova variabile a ogni run
    setLoading(true);

    const paramDate = dateKey(currentDate);

    apiClient
      .get(API_ENDPOINTS.SHIFTS, { params: { date_from: paramDate, date_to: paramDate } })
      .then((res) => {
        if (cancelled) return; // ← legge la cancelled di QUESTA closure
        const items: ServerShift[] = res.data?.data ?? [];
        const { map, hasNight, hasMisaligned } = serverToSlotMap(items, currentDate);
        setShifts(map);
        setBaseline(map); // il diff riparte da zero a ogni caricamento
        if (hasNight) setShowNight(true); // turni oltre mezzanotte → mostro le colonne
        if (hasMisaligned) {
          notification.warning({
            message: "Orari arrotondati",
            description:
              "Alcuni turni avevano orari non allineati ai 30 minuti e sono stati approssimati alla mezz'ora.",
          });
        }
      })
      .catch(() => {
        if (!cancelled) message.error("Errore nel caricamento dei turni");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true; // evita race condition se cambi giorno velocemente, "invalida" QUESTA esecuzione
    };
  }, [currentDate]);

  const toggleGroup = useCallback((name: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const toggleDept = useCallback((groupName: string, deptName: string) => {
    const key = `${groupName}::${deptName}`;
    setCollapsedDepts((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Handler stabili (dipendono solo da currentDate): così React.memo sulle celle
  // funziona davvero e si ri-renderizza solo la cella che cambia.
  const handleCellMouseDown = useCallback(
    (userId: string, slot: number) => {
      isDragging.current = true;
      const key = shiftKey(currentDate, userId, slot);
      setShifts((prev) => {
        dragValue.current = !prev[key]; // direzione del drag decisa dalla prima cella
        const next = { ...prev };
        if (dragValue.current) next[key] = true;
        else delete next[key];
        return next;
      });
    },
    [currentDate]
  );

  const handleCellMouseEnter = useCallback(
    (userId: string, slot: number) => {
      if (!isDragging.current || dragValue.current === null) return;
      const key = shiftKey(currentDate, userId, slot);
      setShifts((prev) => {
        const isFilled = !!prev[key];
        if (isFilled === dragValue.current) return prev; // già nello stato giusto → nessun re-render
        const next = { ...prev };
        if (dragValue.current) next[key] = true;
        else delete next[key];
        return next;
      });
    },
    [currentDate]
  );

  // ── Salvataggio (solo frontend) ──────────────────────────────────────────
  // Salva gli utenti che hanno avuto modifiche, restituendo TUTTI i loro orari
  // attuali (non il diff): il server sostituisce l'intera giornata dell'utente.
  const handleSave = useCallback(() => {
    setSaving(true);

    const shiftsByUser: Record<string, HourRange[]> = {};

    userList.forEach((u) => {
      const uid = String(u.id);
      const currentSlots: number[] = [];
      let changed = false;

      ALL_SLOTS.forEach((s) => {
        const key = shiftKey(currentDate, uid, s);
        const now = !!shifts[key];
        if (now) currentSlots.push(s);
        if (now !== !!baseline[key]) changed = true;
      });

      // Includo solo gli utenti modificati. Array vuoto = tutti i turni rimossi
      // (segnala al server di svuotare la giornata di quell'utente).
      if (changed) {
        shiftsByUser[uid] = slotsToRanges(currentSlots);
      }
    });

    const result = { date: dateKey(currentDate), shifts: shiftsByUser };

    apiClient
      .post(API_ENDPOINTS.SHIFTS_DAILY_GRID, result)
      .then((res) => {
        setBaseline(shifts); // dopo il salvataggio le modifiche diventano il nuovo baseline
        notification.success({
          message: "Turni salvati",
          description: res.data?.message ?? "Modifiche salvate correttamente",
        });
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.message ?? "Errore durante il salvataggio dei turni";
        notification.error({ message: "Salvataggio non riuscito", description: msg });
      })
      .finally(() => setSaving(false));
  }, [shifts, baseline, currentDate, userList]);

  const goToday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setCurrentDate(today);
  }, []);

  // ── Esportazione Excel del giorno corrente ────────────────────────────────
  // GET in blob → scarica il file. Il backend applica il controllo ruolo e la
  // visibilità (manager/vice vedono solo i propri gruppi).
  const handleExport = useCallback(() => {
    setExporting(true);
    const day = dateKey(currentDate);

    apiClient
      .get(API_ENDPOINTS.SHIFTS_DAILY_GRID_EXPORT, {
        params: { date: day },
        responseType: "blob",
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = `turni_${day}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(async (error) => {
        // Con responseType "blob" anche il messaggio d'errore arriva come blob.
        let description = "Errore durante l'esportazione";
        try {
          const text = await error?.response?.data?.text?.();
          if (text) description = JSON.parse(text)?.message ?? description;
        } catch (_) {
          /* lascio il messaggio generico */
        }
        notification.error({ message: "Esportazione non riuscita", description });
      })
      .finally(() => setExporting(false));
  }, [currentDate]);

  return (
    <GridWrapper>
      {/* Barra superiore */}
      <TopBar>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col flex="none">
            <NavGroup>
              <NavBtn onClick={() => setCurrentDate(addDays(currentDate, -1))} title="Giorno precedente">
                <LeftOutlined />
              </NavBtn>
              <NavBtn onClick={() => setCurrentDate(addDays(currentDate, 1))} title="Giorno successivo">
                <RightOutlined />
              </NavBtn>
              <NavBtn onClick={goToday}>Oggi</NavBtn>
              <DatePicker
                allowClear={false}
                inputReadOnly
                format="DD/MM/YYYY"
                value={moment(currentDate)}
                onChange={(m) => m && setCurrentDate(m.startOf("day").toDate())}
              />
              <DayLabel>{formatDate(currentDate)}</DayLabel>
            </NavGroup>
          </Col>

          <Col flex="none">
            <BtnGroup>
              <PrintBtn onClick={handleExport} disabled={exporting} title="Esporta il giorno in Excel">
                <PrinterOutlined /> {exporting ? "Esportazione..." : "Stampa"}
              </PrintBtn>
              <SaveBtn onClick={handleSave} disabled={saving}>
                <SaveOutlined /> {saving ? "Salvataggio..." : "Salva"}
              </SaveBtn>
            </BtnGroup>
          </Col>
        </Row>

        {/* La ricerca su una riga propria: posizione stabile, non dipende
            dalla larghezza variabile della data. */}
        <SearchRow>
          <SearchInputWrap>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              placeholder="Cerca gruppo, dipartimento o dipendente"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchInputWrap>
          <NightToggle>
            <Switch size="small" checked={showNight} onChange={setShowNight} />
            <span>Oltre mezzanotte</span>
          </NightToggle>
        </SearchRow>
      </TopBar>

      {/* Tabella */}
      <Spin spinning={loading} tip="Caricamento turni...">
        <TableScroller
          onMouseLeave={() => {
            isDragging.current = false;
            dragValue.current = null;
          }}
        >
          <StyledTable>
            <thead>
              <tr>
                <StickyTh rowSpan={2} />
                <TotTh rowSpan={2}>TOT</TotTh>
                {hourGroups.map((g) => (
                  <HourGroupTh
                    key={g.startSlot}
                    colSpan={SLOTS_PER_HOUR}
                    night={g.night}
                    midnight={g.midnight}
                  >
                    {g.midnight && <NextDayTag>+1g</NextDayTag>}
                    {g.label}
                  </HourGroupTh>
                ))}
              </tr>
              <tr>
                {visibleSlots.map((s) => (
                  <SlotTickTh key={s} night={isNightSlot(s)} midnight={s === NIGHT_FROM_SLOT}>
                    {slotMinuteLabel(s)}
                  </SlotTickTh>
                ))}
              </tr>
            </thead>

            <tbody>
              {grouped.map(({ groupName, departments }) => {
                const groupCollapsed = collapsedGroups[groupName];
                const userCount = departments.reduce((acc, d) => acc + d.users.length, 0);
                const groupUsersFlat = departments.flatMap((d) => d.users);
                // TOT di gruppo: slot pieni di tutti i membri (incluse ore nascoste)
                const groupTotalSlots = groupUsersFlat.reduce(
                  (tot, u) =>
                    tot +
                    ALL_SLOTS.reduce(
                      (n, s) => (shifts[shiftKey(currentDate, String(u.id), s)] ? n + 1 : n),
                      0
                    ),
                  0
                );
                // Per ogni ora: somma delle ore-uomo coperte dal gruppo in quella fascia
                const hourSlotCounts = hourGroups.map((g) => {
                  let c = 0;
                  for (let k = 0; k < SLOTS_PER_HOUR; k++) {
                    const s = g.startSlot + k;
                    groupUsersFlat.forEach((u) => {
                      if (shifts[shiftKey(currentDate, String(u.id), s)]) c++;
                    });
                  }
                  return c;
                });
                return (
                  <React.Fragment key={groupName}>
                    {/* Riga gruppo: nome + TOT gruppo + somma ore-uomo per ora */}
                    <tr>
                      <GroupTd onClick={() => toggleGroup(groupName)}>
                        <CaretIcon>
                          {groupCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                        </CaretIcon>
                        {groupName}
                        <CountBadge>{userCount}</CountBadge>
                      </GroupTd>
                      <GroupTotTd>{formatHours(groupTotalSlots)}</GroupTotTd>
                      {hourGroups.map((g, i) => (
                        <GroupHourTd key={g.startSlot} colSpan={SLOTS_PER_HOUR} midnight={g.midnight}>
                          {hourSlotCounts[i] > 0 ? formatHours(hourSlotCounts[i]) : ""}
                        </GroupHourTd>
                      ))}
                    </tr>

                    {!groupCollapsed &&
                      departments.map(({ deptName, users }) => {
                        const deptKey = `${groupName}::${deptName}`;
                        const deptCollapsed = collapsedDepts[deptKey];
                        return (
                          <React.Fragment key={deptKey}>
                            {/* Riga dipartimento */}
                            <tr>
                              <DeptTd colSpan={2} onClick={() => toggleDept(groupName, deptName)}>
                                <CaretIcon>
                                  {deptCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                                </CaretIcon>
                                {deptName}
                                <CountBadge muted>{users.length}</CountBadge>
                              </DeptTd>
                              <DeptFiller colSpan={visibleSlots.length} onClick={() => toggleDept(groupName, deptName)} />
                            </tr>

                            {/* Righe utenti */}
                            {!deptCollapsed &&
                              users.map((u) => {
                                const uid = String(u.id);
                                const totalSlots = ALL_SLOTS.reduce(
                                  (n, s) => (shifts[shiftKey(currentDate, uid, s)] ? n + 1 : n),
                                  0
                                );
                                return (
                                  <tr key={uid}>
                                    <StickyUserTd title={`${u.surname} ${u.name}`}>
                                      {u.surname} {u.name.split(" ")[0]}
                                    </StickyUserTd>
                                    <TotTd>{formatHours(totalSlots)}</TotTd>
                                    {visibleSlots.map((s) => {
                                      const key = shiftKey(currentDate, uid, s);
                                      const filled = !!shifts[key];
                                      // estremi del turno: confronto coi vicini
                                      const isRunStart =
                                        filled && !shifts[shiftKey(currentDate, uid, s - 1)];
                                      const isRunEnd =
                                        filled && !shifts[shiftKey(currentDate, uid, s + 1)];
                                      const startHalf = s % SLOTS_PER_HOUR !== 0;
                                      const endHalf = (s + 1) % SLOTS_PER_HOUR !== 0;
                                      // verde chiaro se un estremo cade su mezz'ora
                                      const light =
                                        (isRunStart && startHalf) || (isRunEnd && endHalf);
                                      // label: inizio sulla prima cella, fine sull'ultima
                                      const label = isRunStart
                                        ? slotToTime(s)
                                        : isRunEnd
                                        ? slotToTime(s + 1)
                                        : null;
                                      return (
                                        <ShiftCell
                                          key={s}
                                          userId={uid}
                                          slot={s}
                                          filled={filled}
                                          modified={filled !== !!baseline[key]}
                                          light={light}
                                          label={label}
                                          night={isNightSlot(s)}
                                          midnight={s === NIGHT_FROM_SLOT}
                                          hourStart={isHourStart(s)}
                                          onMouseDown={handleCellMouseDown}
                                          onMouseEnter={handleCellMouseEnter}
                                        />
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })}

              {grouped.length === 0 && (
                <tr>
                  <EmptyTd colSpan={visibleSlots.length + 2}>Nessun risultato per "{search}"</EmptyTd>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </TableScroller>
      </Spin>

      {/* Legenda */}
      <Legend>
        <LegendDot color={CELL_FILLED} /> esistenti
        <LegendDot color={CELL_ADDED} style={{ marginLeft: 14 }} /> aggiunte
        <LegendDot color={CELL_REMOVED} style={{ marginLeft: 14 }} /> rimosse
        <span style={{ marginLeft: 18, fontSize: 12, color: "#aaa" }}>
          Clicca o trascina per selezionare più ore
        </span>
      </Legend>
    </GridWrapper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────

const COL_WIDTH = "190px";
const TOT_WIDTH = "56px"; // colonna TOT (sticky, dopo il nome)
const CELL_WIDTH = "28px"; // larghezza di uno slot (mezz'ora)
const ROW_HEIGHT = "40px";
const HEADER_H1 = 22; // riga header con l'ora
const HEADER_H2 = 18; // riga header con i minuti (:00/:30)

const GridWrapper = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  user-select: none;
`;

const TopBar = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
`;

const TableScroller = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  max-height: 70vh;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;

  th,
  td {
    box-sizing: border-box; /* width include il padding: colonne prevedibili */
    border-right: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
  }
  th:last-child,
  td:last-child {
    border-right: none;
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

const StickyTh = styled.th`
  position: sticky;
  left: 0;
  top: 0;
  z-index: 30;
  background: #fafafa;
  width: ${COL_WIDTH};
  min-width: ${COL_WIDTH};
  height: ${HEADER_H1 + HEADER_H2}px;
`;

const TotTh = styled.th`
  position: sticky;
  left: ${COL_WIDTH};
  top: 0;
  z-index: 30;
  background: #fafafa;
  width: ${TOT_WIDTH};
  min-width: ${TOT_WIDTH};
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  border-right: 1px solid #e0e0e0;
`;

const TotTd = styled.td`
  position: sticky;
  left: ${COL_WIDTH};
  z-index: 5;
  background: #fff;
  width: ${TOT_WIDTH};
  min-width: ${TOT_WIDTH};
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${MAIN};
  border-right: 1px solid #e0e0e0;
`;

const HourGroupTh = styled.th<{ night?: boolean; midnight?: boolean }>`
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: ${({ night }) => (night ? MAIN : "#666")};
  background: ${({ night }) => (night ? NIGHT_HEADER_BG : "#fafafa")};
  position: sticky;
  top: 0;
  z-index: 20;
  height: ${HEADER_H1}px;
  border-left: 1px solid #e6e6e6;
  ${({ midnight }) => midnight && `border-left: 2px solid ${MAIN};`}
`;

const SlotTickTh = styled.th<{ night?: boolean; midnight?: boolean }>`
  width: ${CELL_WIDTH};
  min-width: ${CELL_WIDTH};
  text-align: center;
  font-size: 10px;
  font-weight: 400;
  color: #aaa;
  background: ${({ night }) => (night ? NIGHT_HEADER_BG : "#fafafa")};
  position: sticky;
  top: ${HEADER_H1}px;
  z-index: 20;
  height: ${HEADER_H2}px;
  ${({ midnight }) => midnight && `border-left: 2px solid ${MAIN};`}
`;

const NextDayTag = styled.span`
  display: block;
  font-size: 10px;
  line-height: 1;
  color: ${MAIN};
  margin-bottom: 2px;
`;

const GroupTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 5;
  background: ${MAIN};
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  padding: 9px 16px;
  width: ${COL_WIDTH};
  min-width: ${COL_WIDTH};
  cursor: pointer;
  white-space: nowrap;
`;

const GroupTotTd = styled.td`
  position: sticky;
  left: ${COL_WIDTH};
  z-index: 5;
  background: ${MAIN};
  color: #fff;
  width: ${TOT_WIDTH};
  min-width: ${TOT_WIDTH};
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  border-right: 1px solid rgba(255, 255, 255, 0.25);
`;

const GroupHourTd = styled.td<{ midnight?: boolean }>`
  background: ${MAIN};
  color: #fff;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  ${({ midnight }) => midnight && `border-left: 2px solid #fff;`}
`;

const DeptTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 5;
  background: ${MAIN_LIGHT};
  color: ${MAIN};
  font-size: 14px;
  font-weight: 500;
  padding: 7px 16px 7px 28px;
  width: ${COL_WIDTH};
  min-width: ${COL_WIDTH};
  cursor: pointer;
  white-space: nowrap;
`;

const DeptFiller = styled.td`
  background: ${MAIN_LIGHT};
  cursor: pointer;
`;

const StickyUserTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 5;
  background: #fff;
  font-size: 14px;
  color: #333;
  padding: 0 16px 0 36px;
  width: ${COL_WIDTH};
  min-width: ${COL_WIDTH};
  max-width: ${COL_WIDTH};
  height: ${ROW_HEIGHT};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${MAIN_LIGHTER};
  }
`;

const StyledCell = styled.td<{
  filled: boolean;
  modified: boolean;
  light?: boolean;
  night?: boolean;
  midnight?: boolean;
  hourStart?: boolean;
}>`
  position: relative;
  width: ${CELL_WIDTH};
  min-width: ${CELL_WIDTH};
  height: ${ROW_HEIGHT};
  cursor: pointer;
  transition: background 0.08s;
  background: ${({ filled, modified, light, night }) =>
    filled
      ? modified
        ? CELL_ADDED
        : light
        ? CELL_FILLED_LIGHT
        : CELL_FILLED
      : modified
      ? CELL_REMOVED
      : night
      ? NIGHT_CELL_BG
      : "#fff"};
  ${({ hourStart }) => hourStart && `border-left: 1px solid #e6e6e6;`}
  ${({ midnight }) => midnight && `border-left: 2px solid ${MAIN};`}

  &:hover {
    background: ${({ filled, modified, light }) =>
      filled
        ? modified
          ? CELL_ADDED_HOVER
          : light
          ? CELL_FILLED_LIGHT_HOVER
          : CELL_FILLED_HOVER
        : modified
        ? CELL_REMOVED_HOVER
        : CELL_HOVER};
  }
`;

const CellLabel = styled.span<{ darkText?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: sideways-lr;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: ${({ darkText }) => (darkText ? "#27401C" : "#fff")};
  pointer-events: none; /* non interferisce con click/drag sulla cella */
`;

const CaretIcon = styled.span`
  font-size: 11px;
  margin-right: 8px;
`;

const CountBadge = styled.span<{ muted?: boolean }>`
  margin-left: 8px;
  font-size: 11px;
  font-weight: 400;
  opacity: ${({ muted }) => (muted ? 0.65 : 0.8)};
`;

const EmptyTd = styled.td`
  padding: 24px;
  text-align: center;
  color: #999;
  font-size: 13px;
`;

const DayLabel = styled.span`
  font-size: 20px;
  font-weight: 500;
  color: #333;
  text-transform: capitalize;
  white-space: nowrap;
`;

const NavGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchRow = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const SearchInputWrap = styled.div`
  flex: 1 1 240px;
  max-width: 360px;
`;

const NightToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #888;
  cursor: pointer;
  margin-left: auto;
  white-space: nowrap;
`;

const NavBtn = styled.button`
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #555;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    border-color: ${MAIN};
    color: ${MAIN};
  }
`;

const SaveBtn = styled.button`
  border: 1px solid ${MAIN};
  background: ${MAIN};
  border-radius: 4px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const BtnGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PrintBtn = styled.button`
  border: 1px solid ${MAIN};
  background: ${MAIN};
  border-radius: 4px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 12px;
  color: #888;
  border-top: 1px solid #f0f0f0;
`;

const LegendDot = styled.div<{ color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${({ color }) => color};
`;
